import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';

import SearchSelect, { SelectDataProperties } from '../../../shared/uiComponents/Dropdown/searchSelect';
import { AdminTypes } from '../../../components/Menu';
import { errorNormalizer } from '../../../shared/Helpers/functions';
import { useCheckPermission } from '../../../shared/Helpers/hooks';
import { Client, Provider } from '../../../shared/Assets/sessionDetailsSVGs';
import { PERMISSIONS } from '../../../App/constants';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
	setClient,
	setSearchClient,
	setSearchTherapist,
	setTherapist,
} from '../../../redux/State/clientSlice/calendarHelperSlice';
import { getClinicalSupervisorsTherapists, getUsers } from '../../../redux/State/userSlice';
import { getClients } from '../../../redux/State/clientSlice/clientSlice';
import { getTherapists, getUserClients } from '../../../redux/State/clientSlice/userClientSlice';

// When `controlled` props are supplied, AdminTools uses caller-owned local state instead of the
// global calendar filter (`calendarHelper.filterData`); with no props it behaves as before.
export interface AdminToolsControlledProps {
	client: SelectDataProperties | null;
	provider: SelectDataProperties | null;
	searchClient: string;
	searchProvider: string;
	setClient: (value: SelectDataProperties | null) => void;
	setProvider: (value: SelectDataProperties | null) => void;
	setSearchClient: (value: string) => void;
	setSearchProvider: (value: string) => void;
}

const AdminTools = ({ controlled }: { controlled?: AdminToolsControlledProps }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const USER = useCheckPermission(PERMISSIONS.USER.READ);
	const CLIENT = useCheckPermission(PERMISSIONS.CLIENT.READ);

	const therapists = useSelector((state) => state.userClient.therapists);
	const clinicalSupervisorsTherapists = useSelector((state) => state.clinicalSupervisor.clinicalSupervisorsTherapists);
	const accountId = useSelector((state) => state.account.user.id);
	const role = useSelector((state) => state.account.role);
	const reduxProvider = useSelector((state) => state.calendarHelper.filterData.therapist);
	const reduxSearchProvider = useSelector((state) => state.calendarHelper.filterData.searchTherapist);
	const reduxClient = useSelector((state) => state.calendarHelper.filterData.client);
	const reduxSearchClient = useSelector((state) => state.calendarHelper.filterData.searchClient);

	const choosenUser = controlled ? controlled.provider : reduxProvider;
	const searchString = controlled ? controlled.searchProvider : reduxSearchProvider;
	const choosenClient = controlled ? controlled.client : reduxClient;
	const searchStringClient = controlled ? controlled.searchClient : reduxSearchClient;
	const choosenUserId = choosenUser?.id;
	const choosenClientId = choosenClient?.id;

	// Ref keeps the apply* callbacks stable as effect deps while reading the latest `controlled` prop.
	const controlledRef = useRef(controlled);

	useEffect(() => {
		controlledRef.current = controlled;
	}, [controlled]);

	const applyProvider = useCallback(
		(value: SelectDataProperties | null) => {
			const current = controlledRef.current;
			if (current) current.setProvider(value);
			else dispatch(setTherapist(value));
		},
		[dispatch],
	);

	const applyClient = useCallback(
		(value: SelectDataProperties | null) => {
			const current = controlledRef.current;
			if (current) current.setClient(value);
			else dispatch(setClient(value));
		},
		[dispatch],
	);

	const applySearchProvider = (value: string) => {
		const current = controlledRef.current;
		if (current) current.setSearchProvider(value);
		else dispatch(setSearchTherapist(value));
	};

	const applySearchClient = (value: string) => {
		const current = controlledRef.current;
		if (current) current.setSearchClient(value);
		else dispatch(setSearchClient(value));
	};

	const [users, setUsers] = useState<SelectDataProperties[]>([]);
	const [clients, setClients] = useState<SelectDataProperties[]>([]);
	const [pageSize, setPageSize] = useState<number>(10);
	const [pageSizeClient, setPageSizeClient] = useState<number>(10);
	const [loadMoreClient, setLoadMoreClient] = useState<boolean>(false);
	const [loadMoreUser, setLoadMoreUser] = useState<boolean>(false);

	useEffect(() => {
		if (!USER.permissionGranted) return;
		const clientId = choosenClientId;

		if (!searchString && !controlledRef.current) applyProvider(null);

		const timeout = setTimeout(() => {
			if (!clientId) {
				dispatch(getUsers({ page: '1', pageSize: `${pageSize}`, searchString }))
					.then(unwrapResult)
					.then((response) => {
						const { query, hasNextPage } = response;
						if (!query) return;
						const userData: SelectDataProperties[] = query.map((x) => ({
							id: x.id,
							label: x.fullName,
						}));
						setUsers(userData);
						setLoadMoreUser(Boolean(hasNextPage));
					})
					.catch(errorNormalizer);

				return;
			}
			dispatch(getTherapists({ clientId }));
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [pageSize, searchString, choosenClientId, dispatch, USER.permissionGranted, applyProvider]);

	useEffect(() => {
		if (!CLIENT.permissionGranted) return;
		if (!searchStringClient && !controlledRef.current) applyClient(null);
		const userId = choosenUserId;

		if (userId) {
			const timeout = setTimeout(() => {
				dispatch(
					getUserClients({
						userId,
						page: '1',
						pageSize: `${pageSizeClient}`,
						searchString: searchStringClient,
						insuranceIds: [],
					}),
				)
					.then(unwrapResult)
					.then((response) => {
						const { query, hasNextPage } = response;
						if (!query) return;
						const data: SelectDataProperties[] = query.map((x) => ({
							id: x.id,
							label: x.fullName,
						}));
						setClients(data);
						setLoadMoreClient(Boolean(hasNextPage));
					})
					.catch(errorNormalizer);
			}, 1000);

			// TODO - return void as in return () => void clearTimeout(timeout);
			// eslint-disable-next-line
			return () => clearTimeout(timeout);
		}

		const timeout = setTimeout(() => {
			dispatch(
				getClients({
					page: '1',
					pageSize: `${pageSizeClient}`,
					searchString: searchStringClient,
				}),
			)
				.then(unwrapResult)
				.then((response) => {
					const { query, hasNextPage } = response;
					if (!query) return;
					const data: SelectDataProperties[] = query.map((x) => ({
						id: x.id,
						label: x.fullName,
					}));
					setClients(data);
					setLoadMoreClient(Boolean(hasNextPage));
				})
				.catch(errorNormalizer);
		}, 1000);

		// TODO - return void as in return () => void clearTimeout(timeout);
		// eslint-disable-next-line
		return () => clearTimeout(timeout);
	}, [searchStringClient, dispatch, pageSizeClient, choosenUserId, CLIENT.permissionGranted, applyClient]);

	useEffect(() => {
		if (!therapists?.length) return;

		if (!searchString.length) {
			const list = therapists.map((x) => ({ id: x.id, label: x.fullName }));
			setUsers(list);
			setLoadMoreUser(false);

			return;
		}
		const list = therapists
			.filter((x) => x.fullName.toLowerCase().includes(searchString.toLowerCase()))
			.map((x) => ({ id: x.id, label: x.fullName }));
		setUsers(list);
	}, [therapists, searchString]);

	useEffect(() => {
		if (!clinicalSupervisorsTherapists?.length) return;

		if (!searchString.length) {
			const list = clinicalSupervisorsTherapists.map((x) => ({
				id: x.id,
				label: x.fullName,
			}));
			setUsers(list);
			setLoadMoreUser(false);

			return;
		}
		const list = clinicalSupervisorsTherapists
			.filter((x) => x.fullName.toLowerCase().includes(searchString.toLowerCase()))
			.map((x) => ({ id: x.id, label: x.fullName }));
		setUsers(list);
	}, [clinicalSupervisorsTherapists, searchString]);

	useEffect(() => {
		if (!accountId || role?.section.id !== AdminTypes.cs) return;
		dispatch(getClinicalSupervisorsTherapists(accountId));
	}, [accountId, role, dispatch]);

	const therapistHandler = (value: string) => {
		const user = users.find((x) => x.id === value);
		applyProvider(user ?? null);
	};

	const clientHandler = (value: string) => {
		const client = clients.find((x) => x.id === value);
		applyClient(client ?? null);
	};

	const searchProviderHandler = (value: string) => {
		applySearchProvider(value);
	};

	const searchClientHandler = (value: string) => {
		applySearchClient(value);
	};

	return (
		<>
			{USER.permissionGranted && (
				<SearchSelect
					data={users}
					label="Find Provider:"
					setSearchString={searchProviderHandler}
					searchString={searchString}
					setValue={therapistHandler}
					hasMore={loadMoreUser}
					setSize={setPageSize}
					startAdornment={<Provider />}
				/>
			)}
			{CLIENT.permissionGranted && (
				<SearchSelect
					data={clients}
					label="Find Client:"
					setSearchString={searchClientHandler}
					searchString={searchStringClient}
					setValue={clientHandler}
					hasMore={loadMoreClient}
					setSize={setPageSizeClient}
					startAdornment={<Client />}
				/>
			)}
		</>
	);
};

export default AdminTools;
